import { useCallback, useEffect } from "react";
import { useRouter } from "@tanstack/react-router";
import { useToastStore } from "@/shared/stores/toastStore";
import { useTariffByServiceType } from "@/shared/hooks/useTariffByServiceType";
import { isValidTableId } from "@/shared/lib/tableUtils";
import { useTableDeletion } from "./useTableDeletion";
import { useTableConnection } from "./useTableConnection";
import { useUiStore } from "@/shared/stores/uiStore";
import { useAuthStore } from "@/shared/stores/authStore";
import { useServiceWorkflowStore } from "@/shared/stores/serviceWorkflowStore";
import { useTableInitialization } from "./useTableInitialization";
import { useTableUnsubscribe } from "./useTableUnsubscribe";
import { useTableStore } from "@/shared/stores/tableStore";
import { useTableStatusStore } from "@/shared/stores/tableStatusStore";

export const useServiceWorkflow = () => {
  const router = useRouter();
  const { showToast } = useToastStore();
  const { openModal } = useUiStore();
  const { profile } = useAuthStore();

  const {
    serviceType,
    initStage,
    navigateToStage,
    replaceStage,
    setWorkflowState,
  } = useServiceWorkflowStore();

  const { tableId, setTableId } = useTableStore();

  const {
    tableStatus,
    fetchTableStatus,
    error: tableStatusError,
    reset: resetTableStatus,
  } = useTableStatusStore();

  useEffect(() => {
    return () => {
      resetTableStatus();
    };
  }, [resetTableStatus]);

  useEffect(() => {
    if (initStage) {
      setWorkflowState({
        stage: initStage,
        history: [initStage],
      });
    } else {
      setWorkflowState({
        stage: "loading",
        history: ["loading"],
      });
    }
  }, [initStage, setWorkflowState]);

  const { tableId: initialTableId, isLoading: isSuppliersLoading } =
    useTableInitialization();

  const { tariff } = useTariffByServiceType(serviceType);

  useEffect(() => {
    if (tableId) {
      fetchTableStatus(tableId, tariff, profile);
    }
  }, [tableId, tariff, profile, fetchTableStatus]);

  useEffect(() => {
    setTableId(initialTableId);
  }, [initialTableId, setTableId]);

  const onDeleteSuccess = () => {
    setTableId(null);
    router.history.back();
  };

  const { handleDeleteRequest } = useTableDeletion({
    onDeleteSuccess,
    showToast,
  });

  const handleConnectionSuccess = (newTableId: string) => {
    setTableId(newTableId);
    fetchTableStatus(newTableId, tariff, profile).then(() => {
      navigateToStage("status");
    });
  };

  const handleConnectionError = (message: string) => {
    showToast(message, "error", 4000);
    navigateToStage("stage_1");
  };

  const { isConnecting, tableUrl, setTableUrl, handleConnectTable } =
    useTableConnection({
      onSuccess: handleConnectionSuccess,
      onError: handleConnectionError,
    });

  const { handleUnsubscribeRequest } = useTableUnsubscribe({
    showToast,
    onUnsubscribeSuccess: () => fetchTableStatus(tableId!, tariff, profile),
  });

  useEffect(() => {
    const checkExistingTable = async () => {
      if (isSuppliersLoading) return;

      if (tableId && isValidTableId(tableId)) {
        if (initStage === undefined) {
          replaceStage("status");
        }
      } else {
        if (initStage === undefined) {
          replaceStage("intro");
        }
      }
    };
    checkExistingTable();
  }, [tableId, initStage, isSuppliersLoading, replaceStage]);

  useEffect(() => {
    if (tableStatusError) {
      replaceStage("intro");
    }
  }, [tableStatusError, replaceStage]);

  const handleCopy = useCallback(
    (value: string) => {
      if (!value) return;
      navigator.clipboard
        .writeText(value)
        .then(() => showToast("Текст скопирован", "success"))
        .catch(() => openModal("COPY_ERROR", { textToCopy: value }));
    },
    [showToast, openModal],
  );

  return {
    tableUrl,
    setTableUrl,
    tableStatus,
    isConnecting,
    handleDeleteRequest,
    handleUnsubscribeRequest,
    tariff,
    handleConnectTable,
    handleCopy,
    fetchTableStatus: () => fetchTableStatus(tableId!, tariff, profile),
  };
};
